import { relativeLuminance } from '../releaseThemes';

import {
  WATERMARK_LINE_ADVANCE_EM,
  WATERMARK_LINE_ASCENT_EM,
  WATERMARK_LINE_DESCENT_EM,
  WATERMARK_LINE_PATH,
  WATERMARK_LINE_TEXT,
  WATERMARK_EMBLEM_BARS,
  WATERMARK_LOGO_VIEWBOX,
  WATERMARK_WORD_PATH,
} from './watermarkArt';

/**
 * The Free watermark lockup (design `asset-redesign/w0 Free watermark`, `WatermarkMark.dc.html`;
 * owner rules 2026-10-08 and 2026-10-09). On the Free plan every asset file carries it on every
 * frame: the Music24 logo (the emblem and the word "music24" of the 2026-10-23 brand) and the line "Made with Music24", on a pill.
 * Paid plans have none.
 *
 * Every size is a factor of L, the logo height. Three forms:
 * - `stack`: the logo over the line (portrait and square files);
 * - `row`: the logo, a divider and the line (wide files and the Canvas);
 * - `logo`: the logo alone (the emblem and the word "music24") for a small crop, where the full
 *   lockup does not fit (about 150 px of free space, owner rule 2026-10-09).
 *
 * This file is the one source of the geometry, the colours and the art: the Remotion component
 * (music24-api `KitWatermark`), the web Studio preview, the sml-preview share image and the ffmpeg
 * PNG of the motion artwork all draw `watermarkSvg`. Pure, no imports outside fym-shared.
 */

/** The copy of the line (owner rule 2026-10-09: "Made with Music24"). */
export const WATERMARK_COPY = WATERMARK_LINE_TEXT;

export type WatermarkForm = 'stack' | 'row' | 'logo';
export type WatermarkTone = 'dark' | 'light';

/**
 * The colour tokens of page w0 (`wm-*`). `logotype` is the ink of the word: the brand ink
 * (`assets/music24-brand/README.md`: #F3F1F8 on dark, #0E0C14 on light).
 */
export const WATERMARK_TOKENS: Readonly<
  Record<WatermarkTone, { pill: string; border: string; logotype: string; line: string; divider: string }>
> = {
  dark: {
    pill: 'rgba(10,9,17,0.8)',
    border: 'rgba(255,255,255,0.16)',
    logotype: '#F3F1F8',
    line: '#B7B2C6',
    divider: 'rgba(255,255,255,0.22)',
  },
  light: {
    pill: 'rgba(255,255,255,0.88)',
    border: 'rgba(26,20,54,0.13)',
    logotype: '#0E0C14',
    line: '#514C63',
    divider: 'rgba(26,20,54,0.2)',
  },
};

/**
 * The width of the logo for a height of 1 (the art is 479.49 x 95.66, about 5.01). The logo of the
 * old brand was 190 x 29 (6.55): at the same L every lockup is now narrower, so no layout that held
 * the old lockup overflows.
 */
export const WATERMARK_LOGO_RATIO = WATERMARK_LOGO_VIEWBOX.width / WATERMARK_LOGO_VIEWBOX.height;
/** The line height of the line text (page w0: JetBrains Mono 600, line-height 1.2). */
const LINE_HEIGHT = 1.2;
/** The room between the lockup and the item above it, at L 52 (page w0: 126 + 24 = 150 px on a Story). */
export const WATERMARK_GAP_PER_L = 24 / 52;

/** The geometry of one lockup, in px of the file. */
export interface WatermarkLockup {
  form: WatermarkForm;
  /** The logo height. */
  L: number;
  /** The outer box, the border included. */
  w: number;
  h: number;
  padX: number;
  padY: number;
  border: number;
  radius: number;
  logoW: number;
  /** The font size of the line; 0 for the `logo` form. */
  lineSize: number;
  /** The width of the line; 0 for the `logo` form. */
  lineW: number;
  /** The gap between the logo and the line (stack), or each side of the divider (row). */
  gap: number;
  /** The divider of the row form. Null: none. */
  divider: { w: number; h: number } | null;
  /** The drop shadow: 0 `y` `blur` rgba(0,0,0,.28). */
  shadow: { y: number; blur: number };
  /** The backdrop blur on footage (`glass`). */
  blur: number;
}

const r1 = (value: number): number => Math.round(value * 10) / 10;

/** The lockup of a form at a logo height L (the anatomy table of page w0). Pure. */
export function watermarkLockup(form: WatermarkForm, L: number): WatermarkLockup {
  const border = r1(Math.max(1, L * 0.03));
  const padY = r1(L * 0.3);
  const padX = r1(L * 0.55);
  const logoW = r1(L * WATERMARK_LOGO_RATIO);
  const shadow = { y: r1(L * 0.2), blur: r1(L * 0.6) };
  const blur = r1(L * 0.3);
  if (form === 'logo') {
    const h = r1(L + 2 * padY + 2 * border);
    return {
      form,
      L,
      w: r1(logoW + 2 * padX + 2 * border),
      h,
      padX,
      padY,
      border,
      radius: r1(h / 2),
      logoW,
      lineSize: 0,
      lineW: 0,
      gap: 0,
      divider: null,
      shadow,
      blur,
    };
  }
  if (form === 'row') {
    const lineSize = r1(L * 0.58);
    const lineW = r1(lineSize * WATERMARK_LINE_ADVANCE_EM);
    const gap = r1(L * 0.42);
    const divider = { w: r1(Math.max(2, L * 0.04)), h: r1(L * 0.8) };
    const inner = Math.max(L, lineSize * LINE_HEIGHT, divider.h);
    const h = r1(inner + 2 * padY + 2 * border);
    return {
      form,
      L,
      w: r1(logoW + gap + divider.w + gap + lineW + 2 * padX + 2 * border),
      h,
      padX,
      padY,
      border,
      radius: r1(h / 2),
      logoW,
      lineSize,
      lineW,
      gap,
      divider,
      shadow,
      blur,
    };
  }
  const lineSize = r1(L * 0.5);
  const lineW = r1(lineSize * WATERMARK_LINE_ADVANCE_EM);
  const gap = r1(L * 0.16);
  return {
    form,
    L,
    w: r1(Math.max(logoW, lineW) + 2 * padX + 2 * border),
    h: r1(L + gap + lineSize * LINE_HEIGHT + 2 * padY + 2 * border),
    padX,
    padY,
    border,
    radius: r1(L * 0.45),
    logoW,
    lineSize,
    lineW,
    gap,
    divider: null,
    shadow,
    blur,
  };
}

/**
 * Below this free width (px of the file) a crop is small (owner rule 2026-10-09: "about 150 px of
 * free space"): the line would be too small to read, so the lockup is the logo alone.
 */
export const WATERMARK_SMALL_CROP_PX = 200;

/**
 * The lockup that fits `maxWidth` (owner rule 2026-10-09: on a small crop the lockup shrinks to the
 * logo alone, the emblem and "music24"). The full form when it fits and the crop is not small; else
 * the `logo` form at the same L; else the `logo` form at the L that fits. Pure.
 */
export function fitWatermarkLockup(form: WatermarkForm, L: number, maxWidth: number): WatermarkLockup {
  const full = watermarkLockup(form, L);
  if (full.w <= maxWidth + 0.5 && maxWidth >= WATERMARK_SMALL_CROP_PX) {
    return full;
  }
  const compact = watermarkLockup('logo', L);
  if (compact.w <= maxWidth + 0.5) {
    return compact;
  }
  let fitted = Math.max(1, Math.floor(((L * maxWidth) / compact.w) * 10) / 10);
  let lockup = watermarkLockup('logo', fitted);
  // The parts of the box round to 0.1 px each, so the scaled box can still be a little too wide.
  while (lockup.w > maxWidth + 0.5 && fitted > 1) {
    fitted = Math.max(1, r1(fitted - 0.1));
    lockup = watermarkLockup('logo', fitted);
  }
  return lockup;
}

/** The room above a lockup of logo height L (page w0: 24 px at L 52). Pure. */
export function watermarkGap(L: number): number {
  return r1(L * WATERMARK_GAP_PER_L);
}

/**
 * The tone of the pill (page w0): light when the ground of the theme has a relative luminance of
 * 0.4 or more and the frame shows no footage; else dark. A ground that is not a hex colour is dark. Pure.
 */
export function watermarkTone(groundHex: string | null | undefined, footage: boolean): WatermarkTone {
  if (footage || groundHex === null || groundHex === undefined || !/^#[0-9a-f]{6}$/i.test(groundHex)) {
    return 'dark';
  }
  return relativeLuminance(groundHex) >= 0.4 ? 'light' : 'dark';
}

/**
 * The place of the lockup in each format (page w0, "Placement per format"), in px of the file. `L`
 * is the logo height; the layouts read the rest of the box from `watermarkLockup`.
 */
export const WATERMARK_PLACEMENTS = {
  /** Story, feed post 4:5, lyric card, 9:16 short videos: a stack, L 52, centred at the foot of the safe column. */
  stack1080: { form: 'stack', L: 52 },
  /** The square feed post: the 4:5 stack on a 1350 canvas drawn at 0.8, so L 65 on the canvas. */
  feed11: { form: 'stack', L: 65 },
  /** The AI reel: a stack, L 52, glass, centred x 540, foot y 1440, every frame. */
  aiReel: { form: 'stack', L: 52, cx: 540, bottom: 1440 },
  /** The Spotify Canvas: a row, L 44, centred x 540, from y 1038 (the cover stage ends at y 1010). */
  canvas: { form: 'row', L: 44, cx: 540, top: 1038 },
  /** Banners: a row, L 56 times the unit of the site, the last item of the text column. */
  banner: { form: 'row', L: 56 },
  /** The YouTube thumbnail: a stack, L 40, the last item of the text column. */
  thumbnail: { form: 'stack', L: 40 },
  /** The link preview (the kit file and the sml-preview share image): a stack, L 28, under the badge column. */
  linkPreview: { form: 'stack', L: 28 },
  /**
   * Lyric video, visualizer and music video (Pro-only files; the flag still works): a row, L 31
   * (52 px high), bottom left under the content box (x 120, y 932) on every frame but the end card.
   */
  wide: { form: 'row', L: 31, x: 120, top: 932 },
  /** The music video corner: a row, L 29 (48 px high), its right edge at x 1800, from y 140. */
  musicVideo: { form: 'row', L: 29, right: 1800, top: 140 },
  /** The CTA group of the shared long-video end card: a row, L 20, under the SmartLink line. */
  longVideoEndCard: { form: 'row', L: 20 },
  /** The motion artwork, square: a stack, L 185, glass, centred x 1920, foot y 3610 of 3840. */
  motionSquare: { form: 'stack', L: 185, cx: 1920, bottom: 3610, size: 3840 },
  /** The motion artwork, tall: a stack, L 99, glass, centred, foot 24 px over the foot of the cover band. */
  motionTall: { form: 'stack', L: 99, width: 2048, coverInset: 24 },
  /**
   * The Spotify header (spec 2026-10-17 §2.4): a row, L 40 on the 2660 px file, its right edge at
   * x 1968 (0.74 of the width) and its foot at y 912 (0.80 of the height): in the phone and the
   * desktop crop, right of the name and the photo that Spotify draws bottom left.
   */
  spotifyHeader: { form: 'row', L: 40, right: 1968, bottom: 912, width: 2660 },
} as const;

/** The parts of a lockup inside its box (origin at its top left corner). Pure. */
export function watermarkParts(lockup: WatermarkLockup): {
  logo: { x: number; y: number; h: number };
  divider: { x: number; y: number; w: number; h: number } | null;
  line: { x: number; y: number; size: number } | null;
} {
  const { w, h, padX, padY, border, L, logoW, lineSize, lineW, gap, divider } = lockup;
  if (lockup.form === 'logo') {
    return { logo: { x: (w - logoW) / 2, y: (h - L) / 2, h: L }, divider: null, line: null };
  }
  if (lockup.form === 'row') {
    const logoX = border + padX;
    const dividerX = logoX + logoW + gap;
    const lineX = dividerX + (divider?.w ?? 0) + gap;
    return {
      logo: { x: logoX, y: (h - L) / 2, h: L },
      divider: divider === null ? null : { x: dividerX, y: (h - divider.h) / 2, w: divider.w, h: divider.h },
      line: { x: lineX, y: (h - lineSize * LINE_HEIGHT) / 2, size: lineSize },
    };
  }
  const top = border + padY;
  return {
    logo: { x: (w - logoW) / 2, y: top, h: L },
    divider: null,
    line: { x: (w - lineW) / 2, y: top + L + gap, size: lineSize },
  };
}

const n = (value: number): string => String(Math.round(value * 100) / 100);

/**
 * The lockup as one SVG of `w` x `h` px: the pill, its border, the logo and the line, all as paths
 * (no font). The drop shadow and the backdrop blur are not in it: an HTML renderer adds them as CSS
 * (`watermarkFrameCss`). The art has no gradient and no filter, so it needs no ids: one page can draw
 * many lockups. Pure.
 */
export function watermarkSvg(lockup: WatermarkLockup, tone: WatermarkTone): string {
  const tokens = WATERMARK_TOKENS[tone];
  const parts = watermarkParts(lockup);
  const { w, h, border, radius } = lockup;
  const logoScale = parts.logo.h / WATERMARK_LOGO_VIEWBOX.height;
  const pieces = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${n(w)}" height="${n(h)}" viewBox="0 0 ${n(w)} ${n(h)}" role="img">`,
    // The title names the lockup for a screen reader (the art has no text).
    `<title>${WATERMARK_COPY}</title>`,
    `<rect x="${n(border / 2)}" y="${n(border / 2)}" width="${n(w - border)}" height="${n(h - border)}" rx="${n(
      Math.max(0, radius - border / 2)
    )}" fill="${tokens.pill}" stroke="${tokens.border}" stroke-width="${n(border)}"/>`,
    `<g transform="translate(${n(parts.logo.x)} ${n(parts.logo.y)}) scale(${logoScale.toFixed(5)})">`,
    ...WATERMARK_EMBLEM_BARS.map((bar) => `<path d="${bar.d}" fill="${bar.fill}"/>`),
    `<path d="${WATERMARK_WORD_PATH}" fill="${tokens.logotype}"/>`,
    '</g>',
  ];
  if (parts.divider !== null) {
    const d = parts.divider;
    pieces.push(
      `<rect x="${n(d.x)}" y="${n(d.y)}" width="${n(d.w)}" height="${n(d.h)}" rx="${n(Math.min(2, d.w / 2))}" fill="${
        tokens.divider
      }"/>`
    );
  }
  if (parts.line !== null) {
    const { line } = parts;
    // The line box is 1.2 em high; the content area (ascent 1.02 + descent 0.3) sits in it with a
    // half leading of -0.06 em. The path has its baseline 1.02 em under its origin, so the origin
    // goes to the top of the content area and the baseline lands 0.96 em under the line box top.
    const contentTop =
      line.y + (line.size * (LINE_HEIGHT - (WATERMARK_LINE_ASCENT_EM + WATERMARK_LINE_DESCENT_EM))) / 2;
    const scale = line.size / 1000;
    pieces.push(
      `<path transform="translate(${n(line.x)} ${n(contentTop)}) scale(${scale.toFixed(5)})" d="${WATERMARK_LINE_PATH}" fill="${
        tokens.line
      }"/>`
    );
  }
  pieces.push('</svg>');
  return pieces.join('');
}

/** The SVG of a lockup as a data URL (for an `<img>`). Pure. */
export function watermarkSvgDataUrl(lockup: WatermarkLockup, tone: WatermarkTone): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(watermarkSvg(lockup, tone))}`;
}

/**
 * The CSS of the box that holds the SVG in an HTML renderer: the size, the radius, the drop shadow
 * and, on footage (`glass`), the backdrop blur under the translucent pill. Pure.
 */
export function watermarkFrameCss(lockup: WatermarkLockup, glass: boolean): Record<string, string | number> {
  return {
    width: lockup.w,
    height: lockup.h,
    borderRadius: lockup.radius,
    boxShadow: `0 ${lockup.shadow.y}px ${lockup.shadow.blur}px rgba(0,0,0,.28)`,
    ...(glass
      ? {
          backdropFilter: `blur(${lockup.blur}px) saturate(1.2)`,
          WebkitBackdropFilter: `blur(${lockup.blur}px) saturate(1.2)`,
        }
      : {}),
  };
}

/** A lockup placed in a file: its box (top left, in px) and its geometry. */
export interface PlacedWatermark {
  x: number;
  y: number;
  lockup: WatermarkLockup;
}

/** A lockup centred on `cx` with its foot at `bottom`. Pure. */
export function watermarkAtFoot(lockup: WatermarkLockup, cx: number, bottom: number): PlacedWatermark {
  return { x: r1(cx - lockup.w / 2), y: r1(bottom - lockup.h), lockup };
}

/** The AI reel lockup (page w0: a stack, L 52, centred x 540, foot y 1440, every frame). Pure. */
export function aiReelWatermark(): PlacedWatermark {
  const place = WATERMARK_PLACEMENTS.aiReel;
  return watermarkAtFoot(watermarkLockup(place.form, place.L), place.cx, place.bottom);
}

/** The lockup of a 16:9 video, bottom left (`WATERMARK_PLACEMENTS.wide`). Pure. */
export function wideWatermark(): PlacedWatermark {
  const place = WATERMARK_PLACEMENTS.wide;
  return { x: place.x, y: place.top, lockup: watermarkLockup(place.form, place.L) };
}

/**
 * The lockup of a music video (owner rule 2026-10-08: every frame of a Free file), top right of the
 * safe box (`WATERMARK_PLACEMENTS.musicVideo`). In the song info mode it moves into the CTA group of
 * the end card, so the YouTube end screen zone stays free. Pure.
 */
export function musicVideoWatermark(): PlacedWatermark {
  const place = WATERMARK_PLACEMENTS.musicVideo;
  const lockup = watermarkLockup(place.form, place.L);
  return { x: r1(place.right - lockup.w), y: place.top, lockup };
}

/** The lockup of the CTA group of the shared long-video end card. Pure. */
export function longVideoEndCardLockup(): WatermarkLockup {
  const place = WATERMARK_PLACEMENTS.longVideoEndCard;
  return watermarkLockup(place.form, place.L);
}

/** The Canvas lockup (page w0: a row, L 44, centred x 540, from y 1038). Pure. */
export function canvasWatermark(): PlacedWatermark {
  const place = WATERMARK_PLACEMENTS.canvas;
  const lockup = watermarkLockup(place.form, place.L);
  return { x: r1(place.cx - lockup.w / 2), y: place.top, lockup };
}

/**
 * The lockup of a motion artwork file of any size (page w0; a preview file is half size). Square:
 * a stack centred, its foot at 3610 / 3840 of the height. Tall: a stack centred on the cover, its
 * foot 24 px (at 2048 wide) over the foot of the cover band `cover`. Pure.
 */
export function motionArtworkWatermark(
  frame: { width: number; height: number },
  cover: { y: number; size: number } | null
): PlacedWatermark {
  if (frame.height <= frame.width || cover === null) {
    const place = WATERMARK_PLACEMENTS.motionSquare;
    const k = frame.width / place.size;
    return watermarkAtFoot(
      watermarkLockup(place.form, r1(place.L * k)),
      frame.width / 2,
      frame.height * (place.bottom / place.size)
    );
  }
  const place = WATERMARK_PLACEMENTS.motionTall;
  const k = frame.width / place.width;
  return watermarkAtFoot(
    watermarkLockup(place.form, r1(place.L * k)),
    frame.width / 2,
    cover.y + cover.size - place.coverInset * k
  );
}

/** The lockup of a Spotify header of any width (a Free preview file is half size). Pure. */
export function spotifyHeaderWatermark(frame: { width: number }): PlacedWatermark {
  const place = WATERMARK_PLACEMENTS.spotifyHeader;
  const k = frame.width / place.width;
  const lockup = watermarkLockup(place.form, r1(place.L * k));
  return { x: r1(place.right * k - lockup.w), y: r1(place.bottom * k - lockup.h), lockup };
}

/**
 * The lockup of an artist Profile image of any size (`artistProfile.ts` `ProfileImageSpec.watermark`):
 * a row with L at the full width, scaled to the file; its right edge and its foot at the shares of
 * the place. Under 200 px of free width it is the logo alone. Pure.
 */
export function profileImageWatermark(
  frame: { width: number; height: number },
  place: { L: number; right: number; bottom: number },
  fullWidth: number
): PlacedWatermark {
  const k = frame.width / fullWidth;
  const right = place.right * frame.width;
  const lockup = fitWatermarkLockup('row', r1(place.L * k), right);
  return { x: r1(Math.max(0, right - lockup.w)), y: r1(Math.max(0, place.bottom * frame.height - lockup.h)), lockup };
}
